import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { Meeting } from '@/types';
import { logger } from '@/utils/logger';

// Meeting.participants is `MeetingParticipant[] | string[]`, but a draft only ever
// holds typed-in names — the structured rows exist once the meeting is saved. The
// store's own addParticipant/setParticipants signatures already say string[]; this
// keeps the stored shape, and everything written into it, consistent with them.
export type DraftMeetingInput = Omit<Partial<Meeting>, 'participants'> & {
  participants?: string[];
};

export type DraftMeeting = DraftMeetingInput & {
  draftId: string;
  createdAt: Date;
  lastModified: Date;
};

interface MeetingDraftState {
  // Current active draft
  currentDraft: DraftMeeting | null;
  isDirty: boolean;

  // Draft history (for recovery)
  savedDrafts: DraftMeeting[];
  maxDrafts: number;

  // Autosave
  lastAutosave: Date | null;
  autosaveEnabled: boolean;
}

interface MeetingDraftActions {
  // Draft CRUD
  createDraft: (initialData?: DraftMeetingInput) => string;
  loadDraft: (draftId: string) => void;
  updateDraft: (updates: DraftMeetingInput) => void;
  updateField: <K extends keyof Meeting>(field: K, value: Meeting[K]) => void;
  saveDraft: () => void;
  deleteDraft: (draftId?: string) => void;
  clearCurrentDraft: () => void;

  // Participants
  addParticipant: (participant: string) => void;
  removeParticipant: (participant: string) => void;
  setParticipants: (participants: string[]) => void;

  // Notes
  appendToNotes: (text: string) => void;
  appendToManualNotes: (text: string) => void;

  // Draft management
  getAllDrafts: () => DraftMeeting[];
  getDraft: (draftId: string) => DraftMeeting | undefined;
  clearAllDrafts: () => void;

  // Autosave
  setAutosaveEnabled: (enabled: boolean) => void;
  triggerAutosave: () => void;

  // Reset
  reset: () => void;
}

export type MeetingDraftStore = MeetingDraftState & MeetingDraftActions;

// Generate draft ID
const generateDraftId = () => `draft-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const initialState: MeetingDraftState = {
  currentDraft: null,
  isDirty: false,
  savedDrafts: [],
  maxDrafts: 10,
  lastAutosave: null,
  autosaveEnabled: true,
};

export const useMeetingDraftStore = create<MeetingDraftStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        createDraft: (initialData = {}) => {
          const draftId = generateDraftId();
          const now = new Date();

          const draft: DraftMeeting = {
            ...initialData,
            draftId,
            createdAt: now,
            lastModified: now,
            participants: initialData.participants || [],
            notes: initialData.notes || '',
            manual_notes: initialData.manual_notes || '',
          };

          logger.info('[MeetingDraftStore] Creating draft:', draftId);

          set({
            currentDraft: draft,
            isDirty: false,
          }, false, 'createDraft');

          return draftId;
        },

        loadDraft: (draftId) => {
          const draft = get().savedDrafts.find((d) => d.draftId === draftId);
          if (draft) {
            logger.info('[MeetingDraftStore] Loading draft:', draftId);
            set({
              currentDraft: draft,
              isDirty: false,
            }, false, 'loadDraft');
          } else {
            logger.warn('[MeetingDraftStore] Draft not found:', draftId);
          }
        },

        updateDraft: (updates) => {
          const current = get().currentDraft;
          if (!current) return;

          set({
            currentDraft: {
              ...current,
              ...updates,
              lastModified: new Date(),
            },
            isDirty: true,
          }, false, 'updateDraft');

          // Trigger autosave if enabled
          if (get().autosaveEnabled) {
            get().triggerAutosave();
          }
        },

        updateField: (field, value) => {
          const current = get().currentDraft;
          if (!current) return;

          set({
            currentDraft: {
              ...current,
              [field]: value,
              lastModified: new Date(),
            },
            isDirty: true,
          }, false, 'updateField');

          if (get().autosaveEnabled) {
            get().triggerAutosave();
          }
        },

        saveDraft: () => {
          const current = get().currentDraft;
          if (!current) return;

          const savedDrafts = get().savedDrafts;
          const existingIndex = savedDrafts.findIndex((d) => d.draftId === current.draftId);

          let updatedDrafts: DraftMeeting[];
          if (existingIndex >= 0) {
            // Update existing
            updatedDrafts = [...savedDrafts];
            updatedDrafts[existingIndex] = current;
          } else {
            // Add new, respecting max limit
            updatedDrafts = [current, ...savedDrafts].slice(0, get().maxDrafts);
          }

          logger.info('[MeetingDraftStore] Saving draft:', current.draftId);

          set({
            savedDrafts: updatedDrafts,
            isDirty: false,
            lastAutosave: new Date(),
          }, false, 'saveDraft');
        },

        deleteDraft: (draftId) => {
          const id = draftId || get().currentDraft?.draftId;
          if (!id) return;

          logger.info('[MeetingDraftStore] Deleting draft:', id);

          set((state) => ({
            savedDrafts: state.savedDrafts.filter((d) => d.draftId !== id),
            currentDraft: state.currentDraft?.draftId === id ? null : state.currentDraft,
            isDirty: state.currentDraft?.draftId === id ? false : state.isDirty,
          }), false, 'deleteDraft');
        },

        clearCurrentDraft: () => {
          logger.info('[MeetingDraftStore] Clearing current draft');
          set({
            currentDraft: null,
            isDirty: false,
          }, false, 'clearCurrentDraft');
        },

        // Participant helpers
        addParticipant: (participant) => {
          const current = get().currentDraft;
          if (!current) return;

          const participants = current.participants || [];
          if (!participants.includes(participant)) {
            get().updateDraft({
              participants: [...participants, participant],
            });
          }
        },

        removeParticipant: (participant) => {
          const current = get().currentDraft;
          if (!current) return;

          const participants = current.participants || [];
          get().updateDraft({
            participants: participants.filter((p) => p !== participant),
          });
        },

        setParticipants: (participants) => {
          get().updateDraft({ participants });
        },

        // Notes helpers
        appendToNotes: (text) => {
          const current = get().currentDraft;
          if (!current) return;

          const notes = current.notes || '';
          get().updateDraft({
            notes: notes ? `${notes}\n\n${text}` : text,
          });
        },

        appendToManualNotes: (text) => {
          const current = get().currentDraft;
          if (!current) return;

          const manualNotes = current.manual_notes || '';
          get().updateDraft({
            manual_notes: manualNotes ? `${manualNotes}\n\n${text}` : text,
          });
        },

        // Draft management
        getAllDrafts: () => get().savedDrafts,

        getDraft: (draftId) => get().savedDrafts.find((d) => d.draftId === draftId),

        clearAllDrafts: () => {
          logger.info('[MeetingDraftStore] Clearing all drafts');
          set({
            savedDrafts: [],
            currentDraft: null,
            isDirty: false,
          }, false, 'clearAllDrafts');
        },

        // Autosave
        setAutosaveEnabled: (enabled) => {
          set({ autosaveEnabled: enabled }, false, 'setAutosaveEnabled');
        },

        triggerAutosave: (() => {
          let timeoutId: NodeJS.Timeout | null = null;

          return () => {
            // Debounce autosave by 2 seconds
            if (timeoutId) {
              clearTimeout(timeoutId);
            }

            timeoutId = setTimeout(() => {
              const state = get();
              if (state.isDirty && state.currentDraft) {
                state.saveDraft();
              }
              timeoutId = null;
            }, 2000);
          };
        })(),

        reset: () => {
          set(initialState, false, 'reset');
        },
      }),
      {
        name: 'meetlytic-meeting-drafts',
        // Only persist non-sensitive draft metadata to localStorage.
        // Transcription, notes, summary, and action_items are omitted
        // because they may contain confidential meeting content.
        partialize: (state) => ({
          savedDrafts: state.savedDrafts.map(({ draftId, createdAt, lastModified, title, meeting_date, participants, template }) => ({
            draftId, createdAt, lastModified, title, meeting_date, participants, template,
          })),
          autosaveEnabled: state.autosaveEnabled,
        }),
        // Handle date serialization
        onRehydrateStorage: () => (state) => {
          if (state) {
            // Convert date strings back to Date objects
            state.savedDrafts = state.savedDrafts.map((draft) => ({
              ...draft,
              createdAt: new Date(draft.createdAt),
              lastModified: new Date(draft.lastModified),
            }));
          }
        },
      }
    ),
    { name: 'meeting-draft-store' }
  )
);

// Selector hooks
export const useCurrentDraft = () => useMeetingDraftStore((state) => state.currentDraft);
export const useIsDraftDirty = () => useMeetingDraftStore((state) => state.isDirty);
export const useSavedDrafts = () => useMeetingDraftStore((state) => state.savedDrafts);
export const useDraftCount = () => useMeetingDraftStore((state) => state.savedDrafts.length);

// Combined selectors
export const useDraftState = () => useMeetingDraftStore((state) => ({
  draft: state.currentDraft,
  isDirty: state.isDirty,
  lastAutosave: state.lastAutosave,
  hasDraft: Boolean(state.currentDraft),
}));
